import { useState } from "react";
import {
  base64ToFile,
  convertFileToBase64,
  imageUrlToBlob,
  isBase64File,
  returnHyperKycConfig,
  returnLivenessOptions,
} from "../utils/helpers";
import apiHooks from "./apis";
import { errorToast } from "../utils/makeToast";
import useUserValidated from "./useUserValidated";
import { HypervergeEvent } from "../models/Hyperverge";
import YouverifyPassiveLiveness from "youverify-passive-liveness-web";
import { youVerifyTasks } from "../utils/constants";

const useLivenessCheck = () => {
  const {
    useLogLiveness,
    useBVNFaceMatch,
    useUploadFile,
    useGetHypervergeToken,
    useGetAvailableSdk,
    useGetYouverifyTokenAndSessionId,
    useUserDetails,
    useConvertImageUrlToBase64,
  } = apiHooks;
  const { imageUrlToBase64, gettingBase64 } = useConvertImageUrlToBase64();
  const { handleGetTokenAndSessionId, gettingTokenAndSession, authVars } =
    useGetYouverifyTokenAndSessionId();
  const { handleGetAvailableSdk, gettingAvailableSdk } = useGetAvailableSdk();
  const { handleGetHypervergeToken, gettingToken } = useGetHypervergeToken();
  const { handleFaceMatch, matchingFace } = useBVNFaceMatch();
  const { handleUploadFile, uploadingFile } = useUploadFile();
  const { handleLogLiveness, setLoading, loggingLiveness } = useLogLiveness();
  const { userDetails } = useUserDetails();
  const { handleIsValidated } = useUserValidated();
  const [openModal, setOpenModal] = useState(false);
  const [snapShot, setSnapShot] = useState<string>("");
  const [uploadedImageUrl, setUploadedImageUrl] = useState<string>("");
  const [status, setStatus] = useState<"success" | "failure" | "">("");
  const { token, sessionId } = authVars;
  const { bvn, firstName, lastName, dateOfBirth, nin, userImage } =
    userDetails || {};
  const fullName = `${firstName || ""} ${lastName || ""}`.trim();
  const isLoading =
    loggingLiveness ||
    matchingFace ||
    uploadingFile ||
    gettingToken ||
    gettingAvailableSdk ||
    gettingTokenAndSession ||
    gettingBase64;

  const returnYouVerifyLiveness = (
    sessionId: string,
    sessionToken: string,
  ): YouverifyPassiveLiveness => {
    const yvPassiveLiveness = new YouverifyPassiveLiveness(
      returnLivenessOptions({
        onClose() {},
        onFailure(error) {
          console.error({ error });
          errorToast(error.error.message);
        },
        onSuccess(data) {
          const { passed, faceImage } = data;
          setSnapShot(faceImage);
          handleLivenessLog(passed, faceImage);
        },
        sessionId,
        sessionToken,
      }),
    );
    return yvPassiveLiveness;
  };

  const startLivenessCheck = async () => {
    handleGetAvailableSdk((availableSdk) => {
      try {
        if (availableSdk[0] === "Hyperverge") {
          handleGetHypervergeToken(async (token) => {
            if (HyperKYCModule) {
              await HyperKYCModule?.launch(
                returnHyperKycConfig(token, userImage),
                (event: HypervergeEvent) => {
                  const { status, details, errorMessage } = event;
                  if (status === "error") return errorToast(errorMessage);
                  if (status === "auto_declined")
                    return errorToast(
                      "Identity verification could not be completed due to face mismatch.",
                    );
                  const {
                    Selfie_Validation_Selfie_Image,
                    selfieUrl,
                    selfieValidationResult,
                    faceMatchResult,
                    Selfie_Validation_Action,
                    faceMatchMessage,
                  } = details;
                  const needsReview =
                    status === "needs_review" ||
                    faceMatchResult === "needs_review";
                  const passed =
                    Selfie_Validation_Action === "pass" ||
                    (selfieValidationResult === "pass" &&
                      faceMatchResult === "pass");
                  if (!passed)
                    return errorToast(faceMatchMessage || "Face match failed");
                  const imageUrl = selfieUrl || Selfie_Validation_Selfie_Image;
                  setSnapShot(imageUrl);
                  if (isBase64File(imageUrl))
                    return handleLivenessLog(passed, imageUrl, needsReview);
                  imageUrlToBase64(imageUrl, (base64) => {
                    handleLivenessLog(passed, base64, needsReview);
                  });
                },
              ).catch((error) => console.error({ error }));
            }
          });
        } else {
          handleGetTokenAndSessionId(async (sessionToken, sessionId) => {
            await returnYouVerifyLiveness(sessionId, sessionToken).start(
              youVerifyTasks,
            );
          });
        }
      } catch (error) {
        console.error(error);
        errorToast("Could not launch SDK");
      }
    });
  };

  const handleLivenessLog = (
    state: boolean,
    photo: string = "",
    needsReview: boolean = true,
  ) => {
    handleLogLiveness(
      {
        isSuccess: state,
        name: fullName,
        bvn: bvn || nin || "",
        dateOfBirth,
      },
      () => handleSuccess(state, photo, needsReview),
    );
  };

  const handleSuccess = async (
    state: boolean,
    photo: string = "",
    needsReview: boolean = true,
  ) => {
    setLoading(true);
    const imageIsBase64 = isBase64File(photo);
    const file = imageIsBase64
      ? base64ToFile(photo, `${fullName}.jpeg`)
      : await imageUrlToBlob(photo);
    if (!file) return;
    const runAfterFaceMatch = async () => {
      const postedFilebase64 = imageIsBase64
        ? photo
        : await convertFileToBase64(file);
      handleUploadFile(
        {
          postedFilebase64,
          documentName: "",
          filetype: file.type,
        },
        (url) => {
          setStatus(state ? "success" : "failure");
          setUploadedImageUrl(url);
          handleIsValidated(state);
          setLoading(false);
          returnYouVerifyLiveness(sessionId, token).close();
        },
      );
    };
    if (needsReview === false) return runAfterFaceMatch();
    const formData = new FormData();
    formData.append("Bvn", userDetails?.bvn || "");
    formData.append("NIN", userDetails?.nin || "");
    formData.append("DateOfBirth", userDetails?.dateOfBirth || "");
    formData.append("IdFaceFile", file);
    handleFaceMatch(formData, runAfterFaceMatch, () => setLoading(false));
  };

  return {
    openModal,
    loggingLiveness: isLoading,
    snapShot,
    startLivenessCheck,
    setOpenModal,
    status,
    setStatus,
    setSnapShot,
    uploadedImageUrl,
    setUploadedImageUrl,
  };
};

export default useLivenessCheck;
